/**
 * Viewpoint-style foreman / phase codes: fixed segments 12 + '.' + 4 + '.' + 2,
 * left-padded with spaces. Pure TypeScript — safe for web and NativeScript.
 */

/** Segment lengths: first, second, third. */
export const FOREMAN_PHASE_SEG_LEN: readonly [number, number, number] = [
  12, 4, 2,
];

/** Total display length including two dot literals. */
export const FOREMAN_PHASE_DISPLAY_LEN =
  FOREMAN_PHASE_SEG_LEN[0] +
  1 +
  FOREMAN_PHASE_SEG_LEN[1] +
  1 +
  FOREMAN_PHASE_SEG_LEN[2];

const S0 = FOREMAN_PHASE_SEG_LEN[0];
const S1 = FOREMAN_PHASE_SEG_LEN[1];
const S2 = FOREMAN_PHASE_SEG_LEN[2];

/** Empty template: spaces and literal dots only. */
export const FOREMAN_PHASE_EMPTY_DISPLAY = `${' '.repeat(S0)}.${' '.repeat(S1)}.${' '.repeat(S2)}`;

export type ForemanPhaseSegIndex = 0 | 1 | 2;

/**
 * Canonical 20-character display string from unpadded segment contents.
 */
export function foremanPhaseSegmentsToDisplay(
  seg1: string,
  seg2: string,
  seg3: string
): string {
  const pad = (s: string, n: number) => s.slice(0, n).padStart(n, ' ');
  return `${pad(seg1, S0)}.${pad(seg2, S1)}.${pad(seg3, S2)}`;
}

/**
 * Parses a raw phase string by dots (legacy / freeform) or as one contiguous block.
 */
export function foremanPhaseParseInput(raw: string): {
  part1: string;
  part2: string;
  part3: string;
} {
  const s = raw ?? '';
  const i1 = s.indexOf('.');
  if (i1 === -1) {
    return {
      part1: s.replace(/\./g, ''),
      part2: '',
      part3: '',
    };
  }
  const i2 = s.indexOf('.', i1 + 1);
  if (i2 === -1) {
    return {
      part1: s.slice(0, i1).replace(/\./g, ''),
      part2: s.slice(i1 + 1).replace(/\./g, ''),
      part3: '',
    };
  }
  return {
    part1: s.slice(0, i1).replace(/\./g, ''),
    part2: s.slice(i1 + 1, i2).replace(/\./g, ''),
    part3: s.slice(i2 + 1).replace(/\./g, ''),
  };
}

/** Max characters when typing without `.` (12 + 4 + 2). */
export const FOREMAN_PHASE_TYPING_MAX_CONTINUOUS = S0 + S1 + S2;

/** Max characters when using `.` between segments (includes both dots). */
export const FOREMAN_PHASE_TYPING_MAX_SEGMENTED = FOREMAN_PHASE_DISPLAY_LEN;

/**
 * After the first `.`, when the second segment has more than `S1` characters and there is
 * no second `.` yet, inserts `.` so further typing continues in the third segment.
 * Use with a typed `.` to move to section 2; this handles the 5th character in section 2.
 */
export function foremanPhaseInsertAutoSecondDot(value: string): string {
  const v = value ?? '';
  const i1 = v.indexOf('.');
  if (i1 === -1) {
    return v;
  }
  const i2 = v.indexOf('.', i1 + 1);
  if (i2 !== -1) {
    return v;
  }
  const after = v.slice(i1 + 1);
  if (after.length <= S1) {
    return v;
  }
  return `${v.slice(0, i1 + 1)}${after.slice(0, S1)}.${after.slice(S1)}`;
}

export function foremanPhaseClampTypingLength(value: string): string {
  const v = value ?? '';
  const max = v.includes('.')
    ? FOREMAN_PHASE_TYPING_MAX_SEGMENTED
    : FOREMAN_PHASE_TYPING_MAX_CONTINUOUS;
  return v.length <= max ? v : v.slice(0, max);
}

/**
 * Coerce freeform phase text on each change (e.g. NativeScript single `TextField`).
 * Call `foremanPhaseFormatForSave` on blur/submit for final space padding.
 */
export function foremanPhaseCoerceTypingFreeform(raw: string): string {
  return foremanPhaseClampTypingLength(
    foremanPhaseInsertAutoSecondDot(raw ?? '')
  );
}

/** True if any segment has non-whitespace content. */
export function foremanPhaseInputHasContent(raw: string): boolean {
  const s = raw ?? '';
  if (s.includes('.')) {
    const { part1, part2, part3 } = foremanPhaseParseInput(s);
    return (
      part1.trim().length > 0 ||
      part2.trim().length > 0 ||
      part3.trim().length > 0
    );
  }
  return s.replace(/\./g, '').trim().length > 0;
}

/**
 * Normalizes any phase input to Viewpoint `{12}.{4}.{2}` with space padding.
 * If `raw` is already the fixed canonical display, returns it unchanged.
 */
export function foremanPhaseFormatForSave(raw: string): string {
  const s = raw ?? '';
  if (
    s.length === FOREMAN_PHASE_DISPLAY_LEN &&
    s[S0] === '.' &&
    s[S0 + 1 + S1] === '.'
  ) {
    return s;
  }
  let p1: string;
  let p2: string;
  let p3: string;
  if (s.includes('.')) {
    const parsed = foremanPhaseParseInput(s);
    p1 = parsed.part1.slice(0, S0);
    p2 = parsed.part2.slice(0, S1);
    p3 = parsed.part3.slice(0, S2);
  } else {
    const compact = s.replace(/\./g, '');
    p1 = compact.slice(0, S0);
    p2 = compact.slice(S0, S0 + S1);
    p3 = compact.slice(S0 + S1, S0 + S1 + S2);
  }
  const pad = (seg: string, len: number): string =>
    seg.length >= len ? seg.slice(0, len) : seg.padStart(len, ' ');
  return `${pad(p1, S0)}.${pad(p2, S1)}.${pad(p3, S2)}`;
}

/** Maps caret position in the 20-char display to segment index. */
export function foremanPhaseSegmentIndexFromCaret(
  pos: number
): ForemanPhaseSegIndex {
  if (pos <= S0) return 0;
  if (pos <= S0 + 1 + S1) return 1;
  return 2;
}

/**
 * Caret for right-aligned typing: after last char in segment, or rightmost cell when empty.
 */
export function foremanPhaseCaretPositionForTyping(
  seg: ForemanPhaseSegIndex,
  s1: string,
  s2: string,
  s3: string
): number {
  switch (seg) {
    case 0: {
      const L = s1.length;
      return L === 0 ? S0 - 1 : S0;
    }
    case 1: {
      const L = s2.length;
      const start = S0 + 1;
      return L === 0 ? start + S1 - 1 : start + S1;
    }
    default: {
      const L = s3.length;
      const start = S0 + 1 + S1 + 1;
      return L === 0 ? start + S2 - 1 : start + S2;
    }
  }
}

/** Unpadded segment text from a canonical 20-char display. */
export function foremanPhaseUnpaddedSegmentsFromCanonical(display: string): {
  seg1: string;
  seg2: string;
  seg3: string;
} {
  const d = (display ?? '')
    .padEnd(FOREMAN_PHASE_DISPLAY_LEN, ' ')
    .slice(0, FOREMAN_PHASE_DISPLAY_LEN);
  const strip = (p: string) => p.replace(/^ +/, '').replace(/ +$/, '');
  return {
    seg1: strip(d.slice(0, S0)).slice(0, S0),
    seg2: strip(d.slice(S0 + 1, S0 + 1 + S1)).slice(0, S1),
    seg3: strip(d.slice(S0 + 1 + S1 + 1)).slice(0, S2),
  };
}

/**
 * Mutable field model for interactive phase entry (web key handling or tests).
 * NativeScript can instead bind three `TextField`s and call only the pure formatters above.
 */
export class ForemanPhaseNameFieldModel {
  seg1 = '';
  seg2 = '';
  seg3 = '';
  activeSeg: ForemanPhaseSegIndex = 0;

  get display(): string {
    return foremanPhaseSegmentsToDisplay(this.seg1, this.seg2, this.seg3);
  }

  formattedForSave(): string {
    return foremanPhaseFormatForSave(this.display);
  }

  hasContent(): boolean {
    return foremanPhaseInputHasContent(this.display);
  }

  reset(): void {
    this.seg1 = '';
    this.seg2 = '';
    this.seg3 = '';
    this.activeSeg = 0;
  }

  /** Load segments from any foreman/system phase string. */
  prefillFromRaw(raw: string | null | undefined): void {
    const canonical = foremanPhaseFormatForSave(raw ?? '');
    const u = foremanPhaseUnpaddedSegmentsFromCanonical(canonical);
    this.seg1 = u.seg1;
    this.seg2 = u.seg2;
    this.seg3 = u.seg3;
    if (this.seg1.length < S0) {
      this.activeSeg = 0;
    } else if (this.seg2.length < S1) {
      this.activeSeg = 1;
    } else {
      this.activeSeg = 2;
    }
  }

  advanceSegment(): void {
    if (this.activeSeg < 2) {
      this.activeSeg = (this.activeSeg + 1) as ForemanPhaseSegIndex;
    }
  }

  goToPreviousSegment(): void {
    if (this.activeSeg > 0) {
      this.activeSeg = (this.activeSeg - 1) as ForemanPhaseSegIndex;
    }
  }

  appendChar(ch: string): void {
    const max = FOREMAN_PHASE_SEG_LEN[this.activeSeg];
    const segs: [string, string, string] = [this.seg1, this.seg2, this.seg3];
    const cur = segs[this.activeSeg];
    if (cur.length >= max) return;
    const next = cur + ch;
    segs[this.activeSeg] = next;
    [this.seg1, this.seg2, this.seg3] = segs;
    if (this.activeSeg === 0 && next.length >= S0) {
      this.activeSeg = 1;
    } else if (this.activeSeg === 1 && next.length >= S1) {
      this.activeSeg = 2;
    }
  }

  backspace(): void {
    const segs: [string, string, string] = [this.seg1, this.seg2, this.seg3];
    const cur = segs[this.activeSeg];
    if (cur.length > 0) {
      segs[this.activeSeg] = cur.slice(0, -1);
      [this.seg1, this.seg2, this.seg3] = segs;
      return;
    }
    if (this.activeSeg > 0) {
      this.activeSeg = (this.activeSeg - 1) as ForemanPhaseSegIndex;
      const p = segs[this.activeSeg];
      if (p.length > 0) {
        segs[this.activeSeg] = p.slice(0, -1);
        [this.seg1, this.seg2, this.seg3] = segs;
      }
    }
  }

  applyPaste(text: string): void {
    const trimmed = text.trim();
    let s1 = '';
    let s2 = '';
    let s3 = '';
    if (trimmed.includes('.')) {
      const p = trimmed.split('.');
      s1 = (p[0] ?? '').slice(0, S0);
      s2 = (p[1] ?? '').slice(0, S1);
      s3 = (p[2] ?? '').slice(0, S2);
    } else {
      const compact = trimmed.replace(/\./g, '');
      s1 = compact.slice(0, S0);
      s2 = compact.slice(S0, S0 + S1);
      s3 = compact.slice(S0 + S1, S0 + S1 + S2);
    }
    this.seg1 = s1;
    this.seg2 = s2;
    this.seg3 = s3;
    if (s1.length < S0) {
      this.activeSeg = 0;
    } else if (s2.length < S1) {
      this.activeSeg = 1;
    } else if (s3.length < S2) {
      this.activeSeg = 2;
    } else {
      this.activeSeg = 2;
    }
  }

  caretPosition(): number {
    return foremanPhaseCaretPositionForTyping(
      this.activeSeg,
      this.seg1,
      this.seg2,
      this.seg3
    );
  }

  setActiveSegmentFromCaret(pos: number): void {
    this.activeSeg = foremanPhaseSegmentIndexFromCaret(pos);
  }
}
